import xdk, { device } from '@accedo/xdk-core'
import { ID as WEBOS } from '@accedo/xdk-device-lg-webos'
import { ID as TIZEN } from '@accedo/xdk-device-samsung-tizen'
import { ID as VIZIO } from '@accedo/xdk-device-vizio-smartcast-contrib'
import { v4 as generateUuid } from 'uuid'

import { AnalyticsBrowser } from '@segment/analytics-next'
import NpawPlugin from 'npaw-plugin-es5'
import { getAuthToken } from '../utils/authentication'
import { initDatadog } from '../utils/datadog'
import { initAnalytics } from '../utils/segmentAnalytics'
import { VizbeeReceiver } from '../utils/vizbee/VizbeeReceiver'
import { createYouboraPlugin } from '../utils/youbora'
import { AppSettings } from './appSettings'
import { FloAuth } from './flosports/floAuth'
import { FloContinueWatching } from './flosports/floContinueWatching'
import { FloExperience } from './flosports/floExperience'
import { FloLive } from './flosports/floLlive'
import { SamsungContinueWatching } from './samsungContinueWatching'
import { TizenSubscriptionService } from './subscription/tizenSubscriptionService'
import { VizioSubscriptionService } from './subscription/vizioSubscriptionService'
import { WebosSubscriptionService } from './subscription/webosSubscriptionService'
import { WorkstationSubscriptionService } from './subscription/workstationSubscriptionService'

export class AppConfig {
  public static shared: AppConfig

  public readonly authService: FloAuth
  public readonly liveService: FloLive
  public readonly continueWatchingService: FloContinueWatching
  public readonly samsungCWService: SamsungContinueWatching

  public readonly appSettings: AppSettings
  public readonly analyticsService: AnalyticsBrowser

  public readonly vizbeeReceiver: VizbeeReceiver
  public subscriptionService: ISubscriptionService

  public readonly experienceApi: FloExperience

  public readonly npawPlugin: NpawPlugin

  public readonly sessionId: string

  private constructor() {
    this.sessionId = generateUuid()

    this.appSettings = new AppSettings()
    this.vizbeeReceiver = new VizbeeReceiver()
    this.analyticsService = initAnalytics()

    this.experienceApi = new FloExperience()
    this.authService = new FloAuth()
    this.liveService = new FloLive()
    this.continueWatchingService = new FloContinueWatching()
    this.samsungCWService = new SamsungContinueWatching()
    this.npawPlugin = createYouboraPlugin()
  }

  public static async setup() {
    if (AppConfig.shared) {
      return
    }

    // Wait for XDK to be ready
    const { environment } = xdk
    const xdkReady = new Promise((resolve) => {
      environment.addEventListener(environment.DEVICE.ONLOAD, () => {
        resolve(true)
      })
    })

    if (!device.system) {
      await xdkReady
    }

    // It seems XDK is not waiting on the vizio library to initialize so
    // we need to make sure that the library is fully loaded before proceeding.
    // If the library is loaded early then the handler in preflight will set
    // the variable vizioLibLoaded and we don't need to wait for it.
    const platform = device.platform
    if (platform === VIZIO && !window.vizioLibLoaded) {
      console.log('waiting for vizio library to load')
      const vizioLibReady = new Promise((resolve) => {
        document.addEventListener('VIZIO_LIBRARY_DID_LOAD', () => {
          console.log('vizio library loaded in appConfig')
          resolve(true)
        })
      })
      await vizioLibReady
    }

    AppConfig.shared = new AppConfig()

    if (platform === TIZEN) {
      AppConfig.shared.subscriptionService = new TizenSubscriptionService()
    } else if (platform === WEBOS) {
      AppConfig.shared.subscriptionService = new WebosSubscriptionService()
    } else if (platform === VIZIO) {
      AppConfig.shared.subscriptionService = new VizioSubscriptionService()
    } else {
      AppConfig.shared.subscriptionService = new WorkstationSubscriptionService()
    }

    await AppConfig.shared.appSettings.init()

    const token = getAuthToken()

    if (token) {
      // Prefetch the first two pages of content if user is authenticated
      const homeData = await this.shared.experienceApi.getHome({
        limit: this.shared.experienceApi.homeInitialPageSize * 2,
        offset: 0,
      })

      if (!('error' in homeData)) {
        this.shared.appSettings.homeData = homeData
      }
    }

    AppConfig.shared.onStartup()

    // Datadog init
    initDatadog()
  }

  public onStartup() {
    this.appSettings.trackAppStart()
  }

  public onShutdown() {
    xdk.system.exit()
  }

  public restart() {
    window.location.reload()
  }
}
